import { useEffect, useRef, useState } from 'react';

export type ImagePreloaderState = {
  progress: number;
  isPreloading: boolean;
};

type PriorityImage = HTMLImageElement & { fetchPriority?: 'high' | 'low' | 'auto' };

/**
 * Preloads a set of image URIs in the background and reports load progress.
 * Already-loaded URIs are remembered across renders so re-runs only fetch new images.
 *
 * @param imageUris The list of image URIs to preload.
 * @param priorityUri The active image URI to load first before the rest.
 * @return The current preload progress and whether preloading is still in flight.
 */
export function useImagePreloader(
  imageUris: string[],
  priorityUri?: string
): ImagePreloaderState {
  const [progress, setProgress] = useState(0);
  const preloadedSetRef = useRef<Set<string>>(new Set());

  const priorityUriRef = useRef(priorityUri);
  priorityUriRef.current = priorityUri;

  useEffect(() => {
    if (imageUris.length === 0) {
      setProgress(100);
      return undefined;
    }

    const alreadyLoaded = preloadedSetRef.current;

    if (imageUris.every((uri) => alreadyLoaded.has(uri))) {
      setProgress(100);
      return undefined;
    }

    const total = imageUris.length;
    let loadedCount = imageUris.filter((uri) => alreadyLoaded.has(uri)).length;
    let cancelled = false;
    const inFlight: PriorityImage[] = [];

    const updateProgress = () => {
      if (!cancelled) {
        setProgress(Math.round((loadedCount / total) * 100));
      }
    };

    const loadImage = (uri: string, lowPriority: boolean) =>
      new Promise<void>((resolve) => {
        const img: PriorityImage = new Image();
        if (lowPriority) {
          img.fetchPriority = 'low';
        }
        const onComplete = () => {
          alreadyLoaded.add(uri);
          loadedCount += 1;
          updateProgress();
          resolve();
        };
        img.onload = onComplete;
        img.onerror = onComplete;
        img.src = uri;
        inFlight.push(img);
      });

    updateProgress();

    const run = async () => {
      const priority = priorityUriRef.current;

      if (priority && imageUris.includes(priority) && !alreadyLoaded.has(priority)) {
        await loadImage(priority, false);
        if (cancelled) return;
      }

      imageUris
        .filter((uri) => !alreadyLoaded.has(uri))
        .forEach((uri) => {
          loadImage(uri, true);
        });
    };

    run();

    return () => {
      cancelled = true;
      inFlight.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [imageUris]);

  return { progress, isPreloading: progress < 100 };
}
